// Job-class routing (#510, army-orchestration §1) — a job-class tag routes a task
// to a capability *triple* `(tier, effort, fallback)`, not just a model id. This
// ports the predecessor orchestrator's `TaskComplexity` table verbatim:
// the vocabulary (`[coding]` `[debugging-qa]` `[general]` `[quick]`) is already how
// we write work, so the tag IS the routing key. Pure data + lookups — no I/O.
//
//   - the TIER routes the BYOK pool (`byok_keys.tier`, scored in `llm-credential.ts`)
//     and picks among the models the ACCOUNT named — a key's own model, or a
//     `byok_key_models` row the account tagged with that tier. The platform never
//     turns a tier into a model id of its own;
//   - the EFFORT sets extended-thinking depth (carried; consumed where a stack can);
//   - the FALLBACK chain auto-recovers on model-unavailable (`402/429`, which
//     `classifyLlmFailure` detects) by re-ranking the pool toward the next tier.
//
// REMOVED, do not re-add: a fourth member, `maxBudgetUsd` — a per-run dollar
// ceiling ($10/$5/$3/$1/$5/$8/$1) carried in from the predecessor orchestrator
// when this table was ported. **It was never wired to anything
// here.** `resolveTierRouting` (`apps/worker/src/dispatch/llm-resolver.ts:335`) is
// the only reader of `routeForJobClass` and consumes `tier` + `fallback` only; a
// tree-wide `grep -rn maxBudgetUsd` outside this file returned four COMMENTS and
// no arithmetic. So its docblock — "a cost fence for a runaway loop" — described a
// fence that could not fire, which is the "comment asserting a guarantee the code
// no longer provides" shape. It also could not survive the one test in
// `docs/standards/agent-budgets.md`: "runaway" is named there as a non-mechanism,
// and a bound whose only justification is a bill is deleted, not tuned (#2853 asked
// for the measurement first — the measurement is structural: zero runs can hit a
// ceiling nothing reads). The real runaway detectors measure PROGRESS and live in
// `apps/runner/src/guards.ts` (`stallDetector`, `loopDetector`, `startupDeadline` —
// `runtimeCeiling`, the one wall-clock kill among them, is itself deleted, 2026-09-03).
// The per-session USD hard cap (`TIER_CAPS`) is deleted too (`@developerz/agent`
// `cost.ts` header). The USD ceilings left are budgets their owners chose — the
// account's `spend_cap_usd` and a donated key's cap (`@developerz/billing`
// `metering.ts`) — and neither is this file's.
//
// Table-driven and overridable at dispatch (envelope pin → policy `coding.models`
// → this default) — NOT a hardcoded state machine for policy.

import { z } from 'zod';
import { BYOKTier } from '../entities/byok';
import type { TaskEnvelope } from './task-envelope';

/**
 * A job-class tag. The four `TaskComplexity` tags parsed from work text
 * (`coding|debugging-qa|general|quick`) plus the three fleet roles the design adds
 * (`scout|plan|ci-triage`). Decoupled from model ids — a tag maps to a tier, and a
 * tier selects among the models the account tagged with it (`byok_key_models`).
 */
export const JobClass = {
  /** The big task — new features, hard slices. Default when a `[coding]` tag is written. */
  Coding: 'coding',
  /** Reproducing + fixing, long-context QA. */
  DebuggingQa: 'debugging-qa',
  /** Ordinary edits, review triage — the everyday working class. */
  General: 'general',
  /** Mechanical / rename / doc — cheap and fast. */
  Quick: 'quick',
  /** Fleet: read-only exploration on a scout box, deciding whether an ask is doable. */
  Scout: 'scout',
  /** Fleet: the Planner authoring an epic plan (the biggest reasoning job, #6764). */
  Plan: 'plan',
  /** Fleet: CI failure triage — deterministic-first, cheap. */
  CiTriage: 'ci-triage',
} as const;
export type JobClass = (typeof JobClass)[keyof typeof JobClass];

/**
 * Extended-thinking depth for a job-class. Mirrors the predecessor orchestrator's
 * effort ladder: a big task reasons deepest, a mechanical one
 * barely at all.
 */
export type EffortLevel = 'low' | 'medium' | 'high' | 'max';

/**
 * The `(tier, effort, fallback)` a job-class routes to. Three members, and no
 * spend member — see the `maxBudgetUsd` note in this file's header before adding
 * a fourth that carries a dollar amount.
 */
export type JobClassRoute = {
  /** The BYOK pool tier this class routes to (primary). */
  tier: BYOKTier;
  /** Extended-thinking depth. */
  effort: EffortLevel;
  /**
   * Tier fallback chain, primary first, tried in order when a key reports
   * model-unavailable (`402/429`). Always starts with `tier`.
   */
  fallback: readonly BYOKTier[];
};

/**
 * The routing table — the entire routing system, table-driven (the
 * predecessor orchestrator's model, effort and fallback maps, ported onto our canonical tier ladder). A row is `(tier, effort, fallback)` and
 * carries no spend member: the old per-class dollar ceiling is deleted, and the
 * header says why.
 */
export const JOB_CLASS_ROUTES: Readonly<Record<JobClass, JobClassRoute>> = {
  [JobClass.Coding]: {
    tier: BYOKTier.Heavy,
    effort: 'max',
    fallback: [BYOKTier.Heavy, BYOKTier.Smart, BYOKTier.General],
  },
  [JobClass.DebuggingQa]: {
    tier: BYOKTier.Smart,
    effort: 'high',
    fallback: [BYOKTier.Smart, BYOKTier.General],
  },
  [JobClass.General]: {
    tier: BYOKTier.General,
    effort: 'medium',
    fallback: [BYOKTier.General, BYOKTier.Fast],
  },
  [JobClass.Quick]: {
    tier: BYOKTier.Fast,
    effort: 'low',
    fallback: [BYOKTier.Fast],
  },
  // Fleet roles (design §1.2): scout=smart, plan=heavy, ci-triage=fast.
  /** Reached from `origin: 'scout'` — the read-the-repo-and-decide-doable lane. */
  [JobClass.Scout]: {
    tier: BYOKTier.Smart,
    effort: 'high',
    fallback: [BYOKTier.Smart, BYOKTier.General],
  },
  /**
   * Reached from `origin: 'plan_author'` (#6764) — the Planner, which writes the plan for
   * an ask the scout decided is doable. Its own route and its own model (grill
   * 2026-10-20); the pin purpose is `plan`, published in {@link LLM_ROUTE_PURPOSES} since
   * inception. It was reached by no origin between the `architect` origin's deletion
   * (#2683) and #6764.
   */
  [JobClass.Plan]: {
    tier: BYOKTier.Heavy,
    effort: 'max',
    fallback: [BYOKTier.Heavy, BYOKTier.Smart, BYOKTier.General],
  },
  [JobClass.CiTriage]: {
    tier: BYOKTier.Fast,
    effort: 'low',
    fallback: [BYOKTier.Fast],
  },
};

/** The route for a job-class. Total over {@link JobClass}. */
export function routeForJobClass(jobClass: JobClass): JobClassRoute {
  return JOB_CLASS_ROUTES[jobClass];
}

/**
 * Parse a job-class *complexity* tag from work text (ported
 * from the predecessor orchestrator). Matches `[coding]` / `[quick]` /
 * `[general]` / `[debugging-qa]`, bare or backtick-wrapped, case-insensitive — the
 * exact tags we already write on issue/PR checklists. Only the four complexity tags
 * are text-parseable. The fleet roles are not: `scout` and `ci-triage` are assigned by
 * ORIGIN instead ({@link jobClassForEnvelope}), and so is `plan` (`plan_author`, #6764).
 * Returns `null` when no tag is present.
 */
export function parseJobClassTag(text: string): JobClass | null {
  const tag = JOB_CLASS_TAG.exec(text)?.[1]?.toLowerCase();
  return TAGGABLE_JOB_CLASSES.find((c) => c === tag) ?? null;
}

/**
 * Derive an envelope's job-class. An explicit complexity tag in the title/problem
 * WINS (the author asked for a specific class — the sharpest signal short of an
 * `llm` pin); otherwise the class follows the task's ORIGIN:
 *
 *   - `pr_babysit` / `pr_babysit_watch` → `ci-triage` (CI-fix / mechanical drive-to-merge);
 *   - `scout` → `scout` (smart tier, `high` effort). The one lane that reads a whole
 *     repo and authors the plan every downstream coding task then executes — the
 *     hardest reasoning job we run, and the ONE origin that runs it
 *     (`buildPlanStartEnvelope`, `buildDrainEnvelope`). It routed as `general` on the
 *     cheapest tier for 219 runs in the 7 days before this branch existed, because
 *     the `scout` route was declared but nothing returned it;
 *   - everything else (coding + review origins) → `general`, the safe everyday
 *     working tier. A maintainer fleet does mixed work, so — unlike a pure coding
 *     tool that defaults to the smartest model — an UNTAGGED task must not silently
 *     route every issue to `heavy`. The `[coding]` tag (or a `coding.models` policy
 *     override) is the explicit "this is a big task" signal that escalates it.
 *
 * Pure: reads only the envelope. No I/O, no clock.
 */
export function jobClassForEnvelope(envelope: TaskEnvelope): JobClass {
  const tagged = parseJobClassTag(envelope.title) ?? parseJobClassTag(envelope.problem);
  return tagged ?? jobClassForOrigin(envelope.origin);
}

/**
 * The job class an UNTAGGED task of this origin routes as, where it is not
 * {@link UNTAGGED_DEFAULT_JOB_CLASS} ({@link jobClassForEnvelope}). DATA, so the SQL
 * reading of a task's route purpose (`routePurposeSql`, `@developerz/db`) renders the
 * same table instead of a second spelling of it.
 */
export const ORIGIN_JOB_CLASSES: Readonly<Partial<Record<TaskEnvelope['origin'], JobClass>>> = {
  pr_babysit: JobClass.CiTriage,
  pr_babysit_watch: JobClass.CiTriage,
  scout: JobClass.Scout,
  // The Planner (#6764): its own route and its own model (grill 2026-10-20), Heavy / `max`.
  // The offer task's text is fixed ({@link PLANNER_OFFER_TASK_TITLE}), so no tag outranks it.
  plan_author: JobClass.Plan,
};

/**
 * The Planner offer task's FIXED title and problem (#6764) — what `offerModelCall` stores on
 * the `plan_author` task it mints for a machine. Platform text with NO job-class tag, and
 * never the ask's text: {@link jobClassForEnvelope} and the SQL `routePurposeSql` let a
 * title or problem tag OUTRANK the origin, so an ask carrying `[coding]` copied in here would
 * reroute the plan call off its `plan` route. The precedent is triage's `OFFER_TASK_TITLE` /
 * `offerTaskProblem` (`apps/agents-manager/src/triage-machine-first.ts`).
 * `job-class-routing.planner.test.ts` pins that neither parses as a tag.
 */
export const PLANNER_OFFER_TASK_TITLE = 'Planner: write the plan for a doable ask';
export const PLANNER_OFFER_TASK_PROBLEM =
  "An offer of one structured answer (scout_plan) to this org's idle machines: write the plan " +
  'for an ask the scout decided is doable. Claim it to serve the call through ' +
  'model_call_prompt and answer through model_call_result; nothing else is asked of the box.';

/** The job class an untagged task of any origin outside {@link ORIGIN_JOB_CLASSES} routes as. */
export const UNTAGGED_DEFAULT_JOB_CLASS: JobClass = JobClass.General;

/** The job class an UNTAGGED task of this origin routes as ({@link jobClassForEnvelope}). */
function jobClassForOrigin(origin: TaskEnvelope['origin']): JobClass {
  return ORIGIN_JOB_CLASSES[origin] ?? UNTAGGED_DEFAULT_JOB_CLASS;
}

/** The job classes a title/problem tag can name ({@link parseJobClassTag}). */
export const TAGGABLE_JOB_CLASSES: readonly JobClass[] = [
  JobClass.Coding,
  JobClass.Quick,
  JobClass.General,
  JobClass.DebuggingQa,
];

/**
 * The tag pattern {@link parseJobClassTag} matches, matched case-insensitively: a
 * {@link TAGGABLE_JOB_CLASSES} member in square brackets, optionally backtick-wrapped,
 * captured in group 1. The source both the parser and the SQL reading
 * (`routePurposeSql`, `@developerz/db`) compile, so the two cannot match different tags.
 * Plain enough for both engines (JS and Postgres ARE): literals, an optional backtick,
 * one alternation group.
 */
export const JOB_CLASS_TAG_PATTERN = `\`?\\[(${TAGGABLE_JOB_CLASSES.join('|')})\\]\`?`;
const JOB_CLASS_TAG = new RegExp(JOB_CLASS_TAG_PATTERN, 'i');

// ─── LLM-route purposes (arc 101 slice 03) ────────────────────────────────────

/**
 * The non-{@link JobClass} LLM-route PURPOSES a `llm_routes` pin can target. A
 * route's `purpose` is a `JobClass` value, one of these, or the `default`
 * catch-all — the vocabulary the resolver keys a pin on ("for THIS purpose, use
 * this key/provider/model"). `resolveLlmRoutePin` (`@developerz/db`) applies
 * {@link LlmRoutePurpose.Default} as the fall-through when the task's own purpose
 * has no pinned row.
 */
export const LlmRoutePurpose = {
  /** The AI reviewer + review-thread engagement (`pr_review` / `pr_review_reply`). */
  Review: 'review',
  /** The KB gardener's nightly consolidation model — a janitor, not a task origin. */
  Gardener: 'gardener',
  /** The catch-all row, tried when the task's own purpose has no pin. */
  Default: 'default',
  /**
   * The MODEL ESCALATION rung (2026-09-24): the key/provider/model a coding attempt runs
   * on after its resumed red gate stopped getting closer (`llm-credential/escalation.ts`).
   * Read by exact purpose only — never the `default` catch-all, which names the model
   * the work already runs on — and never a lane a task runs as or a key is fenced to.
   */
  Escalation: 'escalation',
  /**
   * The TRIAGE JUDGE's own pin (#6388): the model that decides each ask, a blocker
   * answer's included. A PIN purpose only, like {@link LlmRoutePurpose.Escalation} —
   * never a lane a task runs as or a key is fenced to. Triage reads this row first and
   * falls back to the `general` route when the account pinned none
   * ({@link TRIAGE_ROUTE_PURPOSE} stays its LANE), so an account that never pins
   * Triage routes exactly as before.
   */
  Triage: 'triage',
  /**
   * The INTAKE BRIEF SCREEN's own pin (#6938): the model that screens a brief for
   * injection before it becomes work. A PIN purpose only, like
   * {@link LlmRoutePurpose.Escalation} — never a lane a task runs as or a key is fenced
   * to. It never follows the `default` row: that row names the account's WORK model,
   * and a park/unpark of it flipped screen verdicts. Repo-scoped pins are read (the
   * screen has a repo id), so it is not in {@link ORG_ONLY_ROUTE_PURPOSES}.
   */
  Screen: 'screen',
} as const;
export type LlmRoutePurpose = (typeof LlmRoutePurpose)[keyof typeof LlmRoutePurpose];

/**
 * The `llm_routes` PURPOSE for one task envelope — the key `resolveLlmRoutePin`
 * looks a pin up by, and the lane the key-purpose fence ({@link isLaneFencePurpose},
 * `lane_fenced`, #1460) compares a designated key against. The review lane (`pr_review` / `pr_review_reply`)
 * routes on {@link LlmRoutePurpose.Review}; every other origin routes on its
 * {@link jobClassForEnvelope} job class. So "pin `review` → key Y" reaches the
 * reviewer (both the diff read and thread engagement) while coding/babysit work
 * keeps routing by job class. (`gardener` is not a task origin — the janitor
 * derives it directly, which is why this can return a {@link LaneFencePurpose}
 * but never `gardener` itself.) Pure: reads only the envelope's origin.
 */
export function routePurposeForEnvelope(envelope: TaskEnvelope): LaneFencePurpose {
  return REVIEW_ROUTE_ORIGINS.includes(envelope.origin)
    ? LlmRoutePurpose.Review
    : jobClassForEnvelope(envelope);
}

/**
 * The GitHub event names (`X-GitHub-Event`, as `inbound_events.event_type` carries them)
 * that are CI reporting on a PR — the events `extractEntity` (`apps/worker`
 * `dispatch/coalesce.ts`) folds onto the PR their suite ran for, and the members the
 * worker's CI wake gate (`dispatch/ci-wake.ts`) grades.
 */
const CI_EVENT_TYPES: readonly string[] = ['check_run', 'check_suite', 'workflow_run'];

/**
 * The `llm_routes` PURPOSE an in-cluster `agent.run` SESSION routes on (owner ruling on
 * #5758, route half: "by job class", the same Agents-page cards as fleet tasks). A PR
 * session woken by CI ({@link CI_EVENT_TYPES}) is CI-fixing work and follows the
 * CI-fixer card ({@link JobClass.CiTriage}), as a babysit task does
 * ({@link jobClassForOrigin}); every other session — a PR review comment, an issue, a
 * push, a release — follows the general route. `entityKind` is the dispatch entity
 * (`DispatchEntityKind`, `@developerz/queue`); `eventType` the event that opened the
 * session's window. Pure.
 */
export function routePurposeForAgentRun(run: {
  readonly entityKind: string;
  readonly eventType: string;
}): JobClass {
  return run.entityKind === 'pr' && CI_EVENT_TYPES.includes(run.eventType)
    ? JobClass.CiTriage
    : JobClass.General;
}

/**
 * The LANE the triage judge runs on — {@link JobClass.General} (#6388). NOT the
 * `triage` PIN row: that row is {@link TRIAGE_PIN_PURPOSE}, the one triage reads
 * FIRST.
 *
 * The LANE and the PIN are DIFFERENT facts, and the split is the point. The lane is
 * where the judge's keys are admitted (the key fence, the capacity admission, the
 * `taskPurpose` a triage pick resolves against), and it stays `general` whether or
 * not an account pins Triage — so an account that never pins Triage routes exactly
 * as before, and an account that does pins only WHICH key the judge runs on.
 */
export const TRIAGE_ROUTE_PURPOSE: LlmRoutePurposeAny = JobClass.General;

/**
 * The PIN purpose triage reads FIRST (#6388) — {@link LlmRoutePurpose.Triage}. The lane,
 * the key fence and capacity admission stay {@link TRIAGE_ROUTE_PURPOSE}; only which
 * route row selects the key changes, and only once an account pins one.
 */
export const TRIAGE_PIN_PURPOSE = LlmRoutePurpose.Triage;

/**
 * The LANE a pin purpose's keys are admitted on — the purpose itself, except the
 * triage pin, whose judge runs on {@link TRIAGE_ROUTE_PURPOSE} (#6388). The Agents
 * page's Triage picker reads this, so it offers exactly the keys the triage judge can
 * be given; a key fenced to `general` stays offered.
 */
export function laneForPinPurpose(purpose: LlmRoutePurposeAny): LlmRoutePurposeAny {
  return purpose === LlmRoutePurpose.Triage ? TRIAGE_ROUTE_PURPOSE : purpose;
}

/**
 * The LANE a task of this ORIGIN routes on when nothing in its text says otherwise — the
 * origin's UNTAGGED lane: a review origin (`pr_review` / `pr_review_reply`) routes on
 * {@link LlmRoutePurpose.Review}, every other origin on its own job class
 * ({@link jobClassForOrigin}).
 *
 * THE WAITING GROUP'S LANE — of which this function is the FALLBACK. Capacity admission
 * reads the account's waiting work grouped (`listWaitingWork`, `@developerz/db`), and a
 * group's lane is the lane its ENVELOPES route on: a `[coding]` tag moves EVERY envelope
 * that carries it, so a group of `[coding]`-tagged envelopes is a CODING-lane group, not
 * an untagged one. The group key's not carrying the title tag is why the LANE is carried
 * on the group, never evidence that the group is untagged.
 *
 * Capacity admission reads a waiting group's ONE purpose from SQL
 * (`listWaitingWork`'s `routePurpose`, read through `routePurposeSql`), not this
 * function: the union over every purpose a tag COULD name over-counted an untagged
 * group on every tagged class's key (#6952). `job-class-routing.test.ts` checks that
 * every envelope's purpose is in its origin's set.
 */
export function routePurposeForOrigin(origin: TaskEnvelope['origin']): LaneFencePurpose {
  if (REVIEW_ROUTE_ORIGINS.includes(origin)) return LlmRoutePurpose.Review;
  return jobClassForOrigin(origin);
}

/**
 * The origins that route on {@link LlmRoutePurpose.Review} — the review arm of
 * {@link routePurposeForEnvelope}, as DATA, for a reader that has the origin but not
 * the envelope: `releaseHoldsOnRouteChange` (`@developerz/db`) filters rows on it in SQL.
 */
export const REVIEW_ROUTE_ORIGINS: readonly TaskEnvelope['origin'][] = [
  'pr_review',
  'pr_review_reply',
];

/**
 * Every value a `llm_routes.purpose` may hold — the full pin vocabulary: each
 * {@link JobClass} plus every {@link LlmRoutePurpose}.
 * The single source of truth the `/v1/llm-routes` edge validates against and the
 * dashboard route editor enumerates its rows from, so neither hardcodes (and
 * drifts behind) the set. Order = job classes first, then the {@link LlmRoutePurpose} extras.
 */
export const LLM_ROUTE_PURPOSES = [
  JobClass.Coding,
  JobClass.DebuggingQa,
  JobClass.General,
  JobClass.Quick,
  JobClass.Scout,
  JobClass.Plan,
  JobClass.CiTriage,
  LlmRoutePurpose.Review,
  LlmRoutePurpose.Gardener,
  LlmRoutePurpose.Default,
  LlmRoutePurpose.Escalation,
  LlmRoutePurpose.Triage,
  LlmRoutePurpose.Screen,
] as const;
/** A single valid route purpose (any {@link JobClass} or {@link LlmRoutePurpose}). */
export type LlmRoutePurposeAny = (typeof LLM_ROUTE_PURPOSES)[number];

/**
 * The purposes that are route PINS ONLY — read by their own reader, never a lane a task
 * runs as, a key is fenced to, or a task-lane selection binds (#6388). The ONE list:
 * `LaneFencePurpose`, `isLaneFencePurpose`, `TASK_LANES`, the key-outage task steps and
 * the selection-failing withdraw all derive from it, so the next pin-only purpose is one
 * line here rather than an edit in each.
 *   - `escalation` — the stronger model a stalled coding attempt climbs to.
 *   - `triage` — the triage judge's own pin, read before the `general` row.
 *   - `screen` — the intake brief screen's own pin, never the `default` row (#6938).
 */
export const PIN_ONLY_PURPOSES = [
  LlmRoutePurpose.Escalation,
  LlmRoutePurpose.Triage,
  LlmRoutePurpose.Screen,
] as const;
export type PinOnlyPurpose = (typeof PIN_ONLY_PURPOSES)[number];

/** Is `value` one of {@link PIN_ONLY_PURPOSES}? */
export function isPinOnlyPurpose(value: string): value is PinOnlyPurpose {
  return (PIN_ONLY_PURPOSES as readonly string[]).includes(value);
}

/**
 * The purposes pinned at ORG scope only (#6388). The triage judge decides an account's
 * asks, and an ask names no single repo, so `triageRoute` reads the org row; a repo row
 * would be stored and never read. The write seam refuses one (`org_only_purpose`).
 */
export const ORG_ONLY_ROUTE_PURPOSES: readonly string[] = [LlmRoutePurpose.Triage];

/** The `org_only_purpose` refusal's sentence — the REST 422 and the MCP tool error both serve it. */
export function orgOnlyPurposeSentence(purpose: string): string {
  return `org_only_purpose: \`${purpose}\` is read at org scope only (its asks name no single repo), so a repo pin would never be read. Pin it at org scope instead (\`llm_route org_pin\`, or the Agents page card).`;
}

/**
 * The lane set {@link isLaneFencePurpose} admits — {@link LLM_ROUTE_PURPOSES}
 * minus {@link LlmRoutePurpose.Default}, DERIVED by subtraction rather than
 * retyped, so a member added to the enumeration joins this union with no second
 * list to remember to extend. Not the same set as {@link TASK_LANES}: that one
 * subtracts `gardener` too, because no task ENVELOPE routes there — but a KEY
 * can be designated `gardener`, so the fence admits it.
 */
export type LaneFencePurpose = Exclude<
  LlmRoutePurposeAny,
  typeof LlmRoutePurpose.Default | PinOnlyPurpose
>;

/**
 * Every LANE a TASK can actually run as — the reachable outputs of
 * {@link routePurposeForEnvelope}, which is what the key lane fence
 * ({@link isLaneFencePurpose}) is compared against at resolution time.
 *
 * DERIVED from {@link LLM_ROUTE_PURPOSES} by subtracting the entries no task
 * envelope can produce, rather than retyped — a new {@link JobClass} joins this set
 * automatically, and the subtraction states WHY each exclusion holds instead of
 * leaving a hand-written list somebody has to remember to extend:
 *
 *   - {@link LlmRoutePurpose.Default} — the route table's catch-all for PIN LOOKUP.
 *     `routePurposeForEnvelope` cannot return it (its `default` branch answers a
 *     job class), so no task ever runs as `default`.
 *   - {@link LlmRoutePurpose.Gardener} — the KB janitor's own model. Not a task
 *     origin at all: the janitor derives the purpose directly, so no envelope
 *     routes to it and no repo's queued work needs a key fenced to it.
 *   - {@link LlmRoutePurpose.Escalation} — the stronger model a STALLED coding attempt
 *     is re-sealed on. A rung above a lane, never a lane: the task still runs as its own
 *     job class, so no key needs fencing to it.
 *   - {@link LlmRoutePurpose.Triage} — the triage JUDGE's PIN, read before the
 *     `general` row (#6388). It is never a LANE because the lane and the pin are
 *     DIFFERENT facts: the lane is where the judge's keys are admitted and it stays
 *     `general` ({@link TRIAGE_ROUTE_PURPOSE}), while the pin only selects WHICH key
 *     the judge runs on ({@link TRIAGE_PIN_PURPOSE}). A consumer that asked an account
 *     for a `triage` key would send an operator to buy one for work no repo queues —
 *     the judge runs on the account's `general` keys.
 *   - {@link LlmRoutePurpose.Screen} — the intake brief screen's PIN (#6938); no task
 *     runs as it and no key is fenced to it.
 *
 * `job-class-routing.test.ts` checks the set equals what {@link routePurposeForEnvelope}
 * computes over every origin and tag, both directions.
 *
 * The one consumer today is `next_steps`' `byok_org_key` rung, which asks whether
 * the account's ORG pool holds a key each lane admits. That question needs the
 * TASK lanes, not the pin vocabulary: reporting an account "not ready" because no
 * key is fenced `gardener` would send an operator to buy a key for work no repo
 * queues.
 */
export const TASK_LANES: readonly LlmRoutePurposeAny[] = LLM_ROUTE_PURPOSES.filter(
  (p) =>
    p !== LlmRoutePurpose.Default &&
    p !== LlmRoutePurpose.Gardener &&
    // The triage judge's PIN, NAMED beside the pin-only subtraction below: the judge
    // runs on the `general` LANE, so `triage` is never a lane a task runs as. The
    // membership set does not change — the pin-only subtraction already excluded it —
    // but the fact is now legible at the derivation instead of inferred from it.
    p !== LlmRoutePurpose.Triage &&
    !isPinOnlyPurpose(p),
);
/** Edge/read validator for a route `purpose` — the whole {@link LLM_ROUTE_PURPOSES} set. */
export const LlmRoutePurposeAnySchema = z.enum(LLM_ROUTE_PURPOSES);

/**
 * Is `value` a LANE a key may be fenced to (#1460)? The route vocabulary MINUS
 * `default` — that entry is the route table's catch-all FALL-THROUGH for pin
 * lookup, never a lane a task runs as ({@link routePurposeForEnvelope} cannot
 * return it), so a key marked `default` could only ever match nothing.
 *
 * Both SIDES of the lane fence run through this, and that is the point: equality
 * alone would let an invalid stored purpose pair with an identically-invalid task
 * purpose (`reviewww` == `reviewww`), so a typo on the key would stop being inert
 * the moment the same typo reached a caller. Validating makes an unrecognised
 * purpose match NOTHING on either side, which is what the fence's fail-safe
 * contract already claimed.
 *
 * It is NOT the only enforcement any more, and saying so was stale: #1490 added
 * the write-edge refusal at the `billing key_purpose_set` action seam
 * (`packages/mcp/src/army/billing.ts` + `packages/db/src/queries/byok-purpose.ts`),
 * which `BYOKKey.purpose`'s own docblock in `entities/byok.ts` already documents.
 * This READ-side validator stays for the reason a `CHECK` constraint was rejected
 * there — the column is text + Zod-at-read, and a value stored before that seam
 * existed must stay INERT (drop the key) rather than throw on read and take the
 * whole pool down over one row.
 */
export function isLaneFencePurpose(value: string | null | undefined): value is LaneFencePurpose {
  return (
    value != null &&
    value !== LlmRoutePurpose.Default &&
    !isPinOnlyPurpose(value) &&
    (LLM_ROUTE_PURPOSES as readonly string[]).includes(value)
  );
}
